import {
    AbstractControl,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from "@angular/forms";

export class CerrixValidators {
    static required(message?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value ? control.value.toString().trim() : "";
            return !value ? { required: message ? { message } : true } : null;
        };
    }

    static conditionalRequired(condition: boolean, message?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value ? control.value.toString().trim() : "";
            return condition && !value ? { required: message ? { message } : true } : null;
        };
    }

    static requiredArray(message?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return control.value?.length ? null : { required: message ? { message } : true };
        };
    }

    static minLength(minLength: number, message?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return Validators.minLength(minLength)(control)
                ? { minLength: message ? { message } : true }
                : null;
        };
    }

    static maxLength(maxLength: number, message?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return Validators.maxLength(maxLength)(control)
                ? { maxLength: message ? { message } : true }
                : null;
        };
    }

    static min(value: number, message: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return Validators.min(value)(control)
                ? { cerrixMin: message ? { message } : true }
                : null;
        };
    }

    static max(value: number, message: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return Validators.max(value)(control)
                ? { cerrixMax: message ? { message } : true }
                : null;
        };
    }

    static maxDate(maxDate?: Date, message?: string, errorKey?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value && maxDate) {
                const date = new Date(control.value);
                if (date.toString() !== "Invalid Date" && date > maxDate) {
                    const result: ValidationErrors = {};
                    result[errorKey ?? "maxDate"] = message ? { message } : true;

                    return result;
                }
            }

            return null;
        };
    }

    static minDate(minDate?: Date, message?: string, errorKey?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value && minDate) {
                const date = new Date(control.value);
                if (date.toString() !== "Invalid Date" && date < minDate) {
                    const result: ValidationErrors = {};
                    result[errorKey ?? "minDate"] = message ? { message } : true;

                    return result;
                }
            }

            return null;
        };
    }

    static emailList(errorKey: string, message?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value) {
                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                const emailList: string[] = control.value.split(";");
                for (let i = 0; i < emailList.length; i++) {
                    if (!emailPattern.test(emailList[i])) {
                        const result: ValidationErrors = {};
                        result[errorKey] = message ? { message } : true;
                        return result;
                    }
                }
            }

            return null;
        };
    }

    static validateWebsite(errorKey: string, message?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value) {
                const websitePattern = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,6}(\/[^\s]*)?$/i;

                const websiteList: string = control.value;
                if (!websitePattern.test(websiteList)) {
                    const result: ValidationErrors = {};
                    result[errorKey] = message ? { message } : true;
                    return result;
                }
            }

            return null;
        };
    }
}

export function getFormErrorMessages(form: AbstractControl): string[] {
    if (form instanceof FormControl) {
        return form.errors
            ? Object.values(form.errors)
                  .filter((e) => e?.message)
                  .map((e) => e.message)
            : [];
    }

    const groupErrors: string[] = [];
    if (form instanceof FormGroup) {
        Object.keys(form.controls).forEach((key) => {
            const errors = getFormErrorMessages(form.get(key)!);
            if (errors.length) {
                groupErrors.push(...errors);
            }
        });
    }

    return groupErrors;
}
